// components/daimon/DaimonOrb.tsx
'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * The Daimon's mark: fater.ai's glass (owner, 2026-09-29: 'it looks like a ball... try the shader we
 * have in our website'), a WebGL shader at every size, the left bar's icon included. The site's
 * Fresnel sphere refracting slow, noise-bent bands of light, in one colour whatever her state. It
 * looks toward the pointer anywhere on the page, a pointer sweep stirs the flow,
 * her voice brightens and quickens it, a click is a push: the glass is shoved away from the finger
 * and springs back while the flow surges (no drawn ripple, owner 2026-09-29). Without WebGL the CSS
 * drawing (.daimon-orb) stands in.
 */
const VERTEX = `attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}`;

const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 uRes;
uniform float uTime;
uniform float uLevel;
uniform vec2 uGaze;
uniform vec3 uDeep;
uniform vec3 uMid;
uniform vec3 uHot;
// fater.ai's hero (components/HeroCanvas.tsx on the marketing site, after monopo.vn): a Fresnel
// glass sphere that refracts, with a slight colour split, and reflects a slow field of noise-bent
// light bands. Ported from three.js (a cube camera and two meshes) to one fragment: each pixel
// casts its ray at the sphere, and the environment the cube camera would have captured is
// computed where the refracted or reflected ray meets the background sphere.
float mod289(float x){return x-floor(x*(1./289.))*289.;}
vec4 mod289(vec4 x){return x-floor(x*(1./289.))*289.;}
vec4 perm(vec4 x){return mod289(((x*34.)+1.)*x);}
float noise(vec3 p){
  vec3 a=floor(p);vec3 d=p-a;d=d*d*(3.-2.*d);
  vec4 b=a.xxyy+vec4(0.,1.,0.,1.);
  vec4 k1=perm(b.xyxy);vec4 k2=perm(k1.xyxy+b.zzww);
  vec4 c=k2+a.zzzz;vec4 k3=perm(c);vec4 k4=perm(c+1.);
  vec4 o1=fract(k3*(1./41.));vec4 o2=fract(k4*(1./41.));
  vec4 o3=o2*d.z+o1*(1.-d.z);vec2 o4=o3.yw*d.x+o3.xz*(1.-d.x);
  return o4.y*d.y+o4.x*(1.-d.y);
}
float hash(vec2 p){p=50.*fract(p*.3183099+vec2(.71,.113));return fract(p.x*p.y*(p.x+p.y));}
float lines(vec2 uv,float offset){return smoothstep(0.,.5+offset*.5,.5*abs(sin(uv.x*45.)+offset*2.));}
mat2 rotate2D(float a){return mat2(cos(a),-sin(a),sin(a),cos(a));}
// The background the glass sees: the site's light bands on a sphere of radius 1.5 around it.
float bands(vec3 from,vec3 dir){
  float b=dot(from,dir);float cc=dot(from,from)-2.25;
  vec3 p=from+dir*(-b+sqrt(max(0.,b*b-cc)));
  float n=noise(p+uTime);
  vec2 uv=rotate2D(n)*p.xy*.06;
  return lines(uv,.1);
}
void main(){
  vec2 c=gl_FragCoord.xy/uRes*2.-1.;
  float r=length(c);
  // A camera close to a unit sphere, its field of view cut to the silhouette so the glass fills the
  // disc. The glass stays centred: moving it toward the pointer let rays at the far rim miss it, a
  // flat smear on the right when the pointer sat bottom left (owner, 2026-09-30). The light inside
  // drifts toward the pointer instead, as the site's does.
  const float D=1.7;
  vec3 eye=vec3(0.,0.,D);
  float halfTan=1./sqrt(D*D-1.);
  vec3 ray=normalize(vec3(c*halfTan*.999,-1.));
  vec3 centre=vec3(0.);
  vec3 lookAt=vec3(uGaze*.08,0.);
  vec3 oc=eye-centre;
  float b=dot(oc,ray);float h=b*b-dot(oc,oc)+1.;
  vec3 P=eye+ray*(-b-sqrt(max(0.,h)));
  vec3 N=normalize(P-centre);
  vec3 R=reflect(ray,N);
  float ratio=1.02;
  float fr=bands(lookAt,normalize(refract(ray,N,ratio)));
  float fg=bands(lookAt,normalize(refract(ray,N,ratio*.99)));
  float fb=bands(lookAt,normalize(refract(ray,N,ratio*.98)));
  float fl=bands(lookAt,vec3(-R.x,R.y,R.z));
  float fres=clamp(.1+4.*pow(1.+dot(ray,N),2.),0.,1.);
  vec3 split=vec3(fr,fg,fb);
  // Colour: the site's bands in her tone over a deep glass, the split kept as a faint prism.
  vec3 lum=mix(split,vec3(fl),fres);
  float l=dot(lum,vec3(.333));
  // Low contrast on purpose: the bands are a sheen across a lit glass, not dark streaks.
  vec3 low=mix(uDeep,uMid,.62);
  vec3 high=mix(uMid,uHot,.3+uLevel*.3);
  vec3 col=mix(low,high,smoothstep(.05,.95,l));
  col+=(lum-l)*.25*uHot;
  col=mix(col,uHot,fres*.3*l);
  col+=(hash(gl_FragCoord.xy)-.5)*.03;
  float a=1.-smoothstep(1.-2./uRes.x,1.,r);
  gl_FragColor=vec4(clamp(col,0.,1.)*a,a);
}
`;

// Deep (limb), mid (disc) and hot (glow) colours, sampled off the owner's references. One colour
// whatever she does (owner, 2026-09-29): a search or a wait on the user shows by its ring, its
// sound and its prompt, never by turning amber or red.
const COLOUR = [[0.30, 0.20, 0.78], [0.30, 0.80, 0.95], [0.80, 1.0, 1.0]] as const;

// The idle breath is for the page's bubble, not the sidebar icon.
const IDLE_MOTION_MIN_SIZE = 64;
const IDLE_BREATH_SECONDS = 5;

type OrbPointer = { x: number; y: number; inside: boolean; dropX: number; dropY: number; dropAt: number };

function useOrbShader(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  size: number,
  levelRef: React.RefObject<number>,
  pointerRef: React.RefObject<OrbPointer>,
  wrapperRef: React.RefObject<HTMLSpanElement | null>,
  frozen: boolean,
) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl', { premultipliedAlpha: true, alpha: true });
    if (!gl) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
    const compile = (type: number, src: string) => {
      const shader = gl.createShader(type)!;
      gl.shaderSource(shader, src);
      gl.compileShader(shader);
      return shader;
    };
    const program = gl.createProgram()!;
    gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(program, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const u = (name: string) => gl.getUniformLocation(program, name);
    const uRes = u('uRes');
    const uLevel = u('uLevel');
    const uTimeLoc = u('uTime');
    const uGaze = u('uGaze');
    const uTone = [u('uDeep'), u('uMid'), u('uHot')] as const;
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(uRes, canvas.width, canvas.height);
    uTone.forEach((location, i) => { const [r, g, b] = COLOUR[i]!; gl.uniform3f(location, r, g, b); });

    const still = frozen || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let clock = 0;
    let last = performance.now();
    let smooth = 0;
    let frame = 0;
    // THE FLOW runs at the site's pace (0.24 a second); a pointer sweep across the orb stirs it (the
    // stir decays over a couple of seconds) and her voice quickens it.
    const BASE_FLOW = 0.24;
    let stir = 0;
    let flow = Math.random() * 40;
    let px = 0;
    let py = 0;
    // THE GAZE: a critically damped spring toward the pointer, wherever it is on the page (its
    // direction from the orb, softened with distance), and a slow wander of its own once the pointer
    // has rested for a few seconds.
    let gazeX = 0;
    let gazeY = 0;
    let gazeVX = 0;
    let gazeVY = 0;
    let seenX = 0;
    let seenY = 0;
    let seenAt = -1e9;
    const onPointer = (event: PointerEvent) => { seenX = event.clientX; seenY = event.clientY; seenAt = performance.now(); };
    window.addEventListener('pointermove', onPointer, { passive: true });
    let lastDrop = 0;
    const draw = (now: number) => {
      const dt = Math.max(1 / 240, Math.min(0.1, (now - last) / 1000));
      last = now;
      smooth += ((levelRef.current ?? 0) - smooth) * 0.15;
      const pointer = pointerRef.current;
      if (pointer.inside) stir = Math.min(3, stir + Math.hypot(pointer.x - px, pointer.y - py) * 1.2);
      px = pointer.x;
      py = pointer.y;
      let aimX = 0.35 * Math.sin(clock * 0.13);
      let aimY = 0.3 * Math.sin(clock * 0.09 + 1);
      if (now - seenAt < 5000) {
        const box = canvas.getBoundingClientRect();
        const half = Math.max(1, box.width / 2);
        const dx = (seenX - box.left - half) / half;
        const dy = (box.top + half - seenY) / half;
        const soften = 1 / (1 + 0.35 * Math.hypot(dx, dy));
        aimX = Math.max(-0.9, Math.min(0.9, dx * soften));
        aimY = Math.max(-0.9, Math.min(0.9, dy * soften));
      }
      const K = 14;
      gazeVX += ((aimX - gazeX) * K - gazeVX * 2 * Math.sqrt(K)) * dt;
      gazeVY += ((aimY - gazeY) * K - gazeVY * 2 * Math.sqrt(K)) * dt;
      gazeX += gazeVX * dt;
      gazeY += gazeVY * dt;
      if (pointer.dropAt && pointer.dropAt !== lastDrop) {
        lastDrop = pointer.dropAt;
        // A push: the glass takes a kick away from the click on its spring, and the flow surges.
        gazeVX -= pointer.dropX * 4;
        gazeVY -= pointer.dropY * 4;
        stir = Math.min(3, stir + 1.2);
      }
      stir *= Math.exp(-dt / 1.4);
      flow += dt * (BASE_FLOW + stir + smooth * 0.5);
      clock += dt;
      gl.uniform1f(uLevel, smooth);
      gl.uniform1f(uTimeLoc, flow);
      gl.uniform2f(uGaze, gazeX, gazeY);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      // She breathes when she talks (owner, 2026-09-28): the bubble swells with her voice, up to
      // about 8%, and settles between words. On the page she is never quite still either (owner,
      // 2026-09-28): an idle breath of about 2% every five seconds and a hair of float. The left
      // bar's icon stays still. Written to the element directly, not through React.
      if (wrapperRef.current) {
        const idle = size >= IDLE_MOTION_MIN_SIZE ? 0.5 - 0.5 * Math.cos((clock / IDLE_BREATH_SECONDS) * Math.PI * 2) : 0;
        const scale = 1 + Math.max(idle * 0.02, Math.min(1, smooth) * 0.08);
        const float = idle ? (idle - 0.5) * -0.012 * size : 0;
        wrapperRef.current.style.transform = scale > 1.0005 || float ? `translateY(${float.toFixed(2)}px) scale(${scale.toFixed(4)})` : '';
      }
      if (!still) frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    // No loseContext() here: a canvas hands back the SAME context on the next getContext, so a
    // remount (React dev runs every effect twice) drew into a dead context and showed a white disc.
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onPointer);
    };
  }, [canvasRef, size, levelRef, pointerRef, wrapperRef, frozen]);
}

// A triple click shows her full screen, alone on black (owner, 2026-09-29: 'in any situation').
const fullScreenSide = () => Math.round(Math.min(window.innerWidth, window.innerHeight) * 0.62);

function FullScreenDaimon({ level, onClose }: { level: number; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [side, setSide] = useState(fullScreenSide);
  const close = useRef(onClose);
  useEffect(() => { close.current = onClose; }, [onClose]);
  useEffect(() => {
    // Real full screen where the browser allows it; the black layer alone otherwise.
    void ref.current?.requestFullscreen?.().catch(() => undefined);
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close.current(); };
    const onExit = () => { if (!document.fullscreenElement) close.current(); };
    const onResize = () => setSide(fullScreenSide());
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onExit);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onExit);
      window.removeEventListener('resize', onResize);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, []);
  return createPortal(
    // A portal still bubbles through the React tree: the orb's host (a button, a draggable bubble)
    // must not hear what happens in here.
    <div ref={ref} data-daimon-ui className="fixed inset-0 z-[2147483000] flex items-center justify-center bg-black" role="dialog" aria-label="The Daimon, full screen"
      onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()}>
      <DaimonOrb size={side} level={level} onTripleClick={onClose} />
    </div>,
    document.body,
  );
}

// `still` draws one frame: any frame the page keeps producing holds the GPU out of idle, measured at
// ~2.5% on the owner's machine whatever the pixel count or the frame rate (5 to 60 fps).
export const DaimonOrb = ({ size, level = 0, still = false, onTripleClick, children }: { size: number; level?: number; still?: boolean; onTripleClick?: () => void; children?: React.ReactNode }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const levelRef = useRef(level);
  useEffect(() => { levelRef.current = level; }, [level]);
  const pointerRef = useRef<OrbPointer>({ x: 0, y: 0, inside: false, dropX: 0, dropY: 0, dropAt: 0 });
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  useOrbShader(canvasRef, size, levelRef, pointerRef, wrapperRef, still);

  const [full, setFull] = useState(false);
  const closeFull = React.useCallback(() => setFull(false), []);
  // Clicks are held a moment to tell one click from three: a single one then reaches whatever the
  // orb sits in (talk, go back to her) as before, a triple never does. Keyboard clicks (detail 0)
  // and the replayed click pass straight through.
  const heldClick = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (heldClick.current) clearTimeout(heldClick.current); }, []);
  const onClickCapture = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (event.detail === 0) return;
    event.stopPropagation();
    event.preventDefault();
    if (heldClick.current) clearTimeout(heldClick.current);
    heldClick.current = null;
    if (event.detail >= 3) {
      if (onTripleClick) onTripleClick(); else setFull(true);
      return;
    }
    const orb = event.currentTarget;
    heldClick.current = setTimeout(() => { heldClick.current = null; orb.click(); }, 320);
  };

  const toOrb = (event: React.PointerEvent<HTMLSpanElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return { x: ((event.clientX - box.left) / box.width) * 2 - 1, y: 1 - ((event.clientY - box.top) / box.height) * 2 };
  };

  return (
    <>
    {full ? <FullScreenDaimon level={level} onClose={closeFull} /> : null}
    <span
      ref={wrapperRef}
      aria-hidden
      className="daimon-orb relative block"
      style={{ width: size, height: size }}
      onPointerMove={(event) => Object.assign(pointerRef.current, toOrb(event), { inside: true })}
      onPointerLeave={() => { pointerRef.current.inside = false; }}
      onClickCapture={onClickCapture}
      onPointerDown={(event) => {
        const at = toOrb(event);
        Object.assign(pointerRef.current, { dropX: at.x, dropY: at.y, dropAt: performance.now() });
      }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full rounded-full" style={{ width: size, height: size }} />
      {/* Her rings live in this exact square, which is what breathes and swells: a ring drawn on any
          outer box stays put while the ball moves, and comes out off centre (owner, 2026-10-10). */}
      {children}
    </span>
    </>
  );
};
